import {
  DEFAULT_COURSES,
  RAISE_PER_LEVEL,
  careerEased,
  careerLevel,
  careerTitle,
  formatPence,
  shiftsForLevel,
  shiftsToNextLevel,
  wageFor,
  type Progress,
} from '@london-life/contracts';
import { useCatalogue } from '../lib/content';

// work builds a career at each workplace: a title, and a pay rise every level
export function CareerTab({
  progress,
  earnedTodayPence,
  capPence,
}: {
  progress: Progress;
  earnedTodayPence: number;
  capPence: number;
}) {
  // the jobs and where they are come from the published content
  const catalogue = useCatalogue();
  const jobs = catalogue?.jobs ?? [];
  const JOB_KEYS = jobs.map((job) => job.id);
  const activityOf = (id: string) => catalogue?.activity(id);
  const workplace = (id: string) => catalogue?.place(activityOf(id)?.placeId ?? '')?.name ?? '';
  const titles = (id: string) => activityOf(id)?.job?.titles ?? [];
  // a course certificate for the job: promotions come sooner (courses.ts)
  const easedFor = (id: string) => careerEased(catalogue?.courses ?? DEFAULT_COURSES, progress.courses, id);
  const worked = JOB_KEYS.filter((job) => (progress.jobs[job] ?? 0) > 0).sort(
    (a, b) => (progress.jobs[b] ?? 0) - (progress.jobs[a] ?? 0),
  );
  const open = JOB_KEYS.filter((job) => !worked.includes(job));
  const main = worked[0];
  return (
    <div className="profile-grid">
      <section className="profile-card" aria-labelledby="career-title">
        <h3 id="career-title">Career</h3>
        {main ? (
          <p className="career-main">
            <strong>{careerTitle(activityOf(main) ?? {}, progress.jobs[main] ?? 0, easedFor(main))}</strong>
            <span>at {workplace(main)}</span>
          </p>
        ) : (
          <p className="profile-hint">
            No job yet. Work a shift at the office, café, market, Billingsgate or the Tube.
          </p>
        )}
        <p className="profile-hint">
          Earned today: <strong>{formatPence(earnedTodayPence)}</strong> of {formatPence(capPence)}. Each level adds{' '}
          {Math.round(RAISE_PER_LEVEL * 100)}% to your pay.
        </p>
      </section>
      {worked.map((job) => {
        const shifts = progress.jobs[job] ?? 0;
        const eased = easedFor(job);
        const level = careerLevel(shifts, eased);
        const next = shiftsToNextLevel(shifts, eased);
        const from = shiftsForLevel(level, eased);
        const share = next === null ? 100 : ((shifts - from) / (shifts + next - from)) * 100;
        const base = activityOf(job)?.wagePence ?? 0;
        return (
          <section key={job} className="profile-card" aria-label={workplace(job)}>
            <h3>
              {workplace(job)} <span className="skills-level">Level {level}</span>
            </h3>
            <p className="career-main">
              <strong>{careerTitle(activityOf(job) ?? {}, shifts, eased)}</strong>
              <span>{formatPence(wageFor(base, shifts, eased))} a shift</span>
            </p>
            <span
              className="needs-bar"
              role="meter"
              aria-label={`${workplace(job)} progress`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(share)}
            >
              <b
                style={{
                  width: `${share}%`,
                  background: 'var(--primary)',
                }}
              />
            </span>
            <p className="profile-hint">
              {shifts} shift{shifts === 1 ? '' : 's'} worked.{' '}
              {next === null
                ? 'Top of the ladder.'
                : `${next} more to become ${titles(job)[level]} (${formatPence(wageFor(base, shifts + next, eased))} a shift).`}
            </p>
          </section>
        );
      })}
      {open.length > 0 && (
        <section className="profile-card" aria-labelledby="career-open">
          <h3 id="career-open">Other work</h3>
          <ul className="traits-list">
            {open.map((job) => (
              <li key={job}>
                <em aria-hidden="true">{activityOf(job)?.icon}</em>
                <span>
                  <strong>{titles(job)[0]}</strong>
                  <small>
                    {workplace(job)} · {formatPence(activityOf(job)?.wagePence ?? 0)} a shift
                  </small>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
